import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Users, FileText, TrendingUp, PlusCircle, ArrowRight } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CapTableChart } from '@/components/ledger/CapTableChart';
import { LedgerEventsFeed } from '@/components/ledger/LedgerEventsFeed';
import { ShareholderDialog } from '@/components/shareholder/ShareholderDialog';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { FirstRunDashboard } from '@/components/dashboard/FirstRunDashboard';
import { useTenantStore } from '@/hooks/useTenantStore';
import { Button } from '@/components/ui/button';
import * as CapLedgerService from '@/services/capLedgerService';

interface Stats {
  totalShares: number;
  shareholders: number;
  ledgerEvents: number;
  companyValuation: number;
}

interface CompanyDetails {
  name: string;
  registration_number?: string;
  jurisdiction?: string;
  incorporation_date?: string;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { tenantId } = useTenantStore();
  const [stats, setStats] = useState<Stats>({
    totalShares: 0,
    shareholders: 0,
    ledgerEvents: 0,
    companyValuation: 0,
  });
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [shareholderDialogOpen, setShareholderDialogOpen] = useState(false);
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [showFirstRun, setShowFirstRun] = useState<boolean | null>(null);

  useEffect(() => {
    if (tenantId) {
      fetchStats();
      fetchCompanyDetails();
      checkFirstRun();
    } else {
      setLoading(false);
      setShowFirstRun(null);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const data = await CapLedgerService.getCompanies();
      setCompanies(data.map((c) => ({ id: c.id, name: c.name })));
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const checkFirstRun = async () => {
    if (!tenantId) return;
    const dismissed = localStorage.getItem(`first-run-dismissed-${tenantId}`);
    if (dismissed === 'true') {
      setShowFirstRun(false);
      return;
    }
    // Show first-run if company has few ledger events and shareholders
    const [{ count: ledgerCount }, { count: shareholderCount }] = await Promise.all([
      supabase
        .from('ledger_events')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', tenantId),
      supabase
        .from('shareholders')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', tenantId),
    ]);
    setShowFirstRun((ledgerCount || 0) <= 3 && (shareholderCount || 0) <= 3);
  };

  const fetchCompanyDetails = async () => {
    if (!tenantId) return;

    try {
      const company = await CapLedgerService.getCompanyById(tenantId);
      if (company) {
        setCompanyDetails({
          name: company.name,
          registration_number: company.registration_number,
          jurisdiction: company.jurisdiction,
          incorporation_date: company.incorporation_date,
        });
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
    }
  };

  const fetchStats = async () => {
    if (!tenantId) return;

    try {
      setLoading(true);

      const [capTableSummary, shareholdersRes, ledgerEventsRes] = await Promise.all([
        CapLedgerService.getCapTableSummary(tenantId),
        supabase
          .from('shareholders')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', tenantId),
        supabase
          .from('ledger_events')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', tenantId),
      ]);

      const totalShares = capTableSummary.reduce(
        (sum, entry) => sum + (entry.total_shares || 0),
        0
      );

      setStats({
        totalShares,
        shareholders: shareholdersRes.count || 0,
        ledgerEvents: ledgerEventsRes.count || 0,
        companyValuation: 0, // Placeholder for future calculation
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Shares',
      value: stats.totalShares.toLocaleString(),
      icon: TrendingUp,
      description: 'Shares issued',
    },
    {
      title: 'Shareholders',
      value: stats.shareholders,
      icon: Users,
      description: 'Active shareholders',
    },
    {
      title: 'Ledger Events',
      value: stats.ledgerEvents,
      icon: FileText,
      description: 'Total transactions',
    },
    {
      title: 'Valuation',
      value: stats.companyValuation > 0 ? `R ${stats.companyValuation.toLocaleString()}` : '—',
      icon: Building2,
      description: 'Company valuation',
    },
  ];

  if (!tenantId) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Welcome to CapLedger</CardTitle>
              <CardDescription>
                Please select a company from the dropdown above to view your dashboard
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (showFirstRun && companyDetails) {
    return (
      <DashboardLayout>
        <FirstRunDashboard
          companyId={tenantId}
          companyName={companyDetails.name}
          onDismiss={() => {
            localStorage.setItem(`first-run-dismissed-${tenantId}`, 'true');
            setShowFirstRun(false);
          }}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Company Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {companyDetails?.name || 'Dashboard'}
          </h1>
          <p className="text-muted-foreground">
            {companyDetails?.registration_number && `Reg: ${companyDetails.registration_number}`}
            {companyDetails?.jurisdiction && ` · ${companyDetails.jurisdiction}`}
          </p>
        </div>

        {/* Onboarding Checklist */}
        <OnboardingChecklist />

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-4" data-tour="stats-cards">
          {statCards.map((stat) => (
            <Card key={stat.title} variant="stat">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {stat.title}
                </p>
                <stat.icon className="h-4 w-4 text-muted-foreground/60" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold tabular-nums">
                  {loading ? '...' : stat.value}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Cap Table Chart and Quick Actions */}
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2" data-tour="cap-table-chart">
            <CapTableChart companyId={tenantId} />
          </div>

          <Card data-tour="quick-actions">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common tasks and shortcuts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={() => setShareholderDialogOpen(true)}
              >
                <PlusCircle className="h-4 w-4" />
                Add Shareholder
              </Button>

              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={() => navigate('/settings?tab=team-access')}
              >
                <Users className="h-4 w-4" />
                Manage Team & Access
              </Button>

              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={() => navigate('/shareholders')}
              >
                <Users className="h-4 w-4" />
                View Shareholders
              </Button>

              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={() => navigate('/transactions')}
              >
                <FileText className="h-4 w-4" />
                View Transactions
              </Button>

              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={() => navigate('/companies')}
              >
                <TrendingUp className="h-4 w-4" />
                View Cap Table
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest ledger events for this company</CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/transactions')}
              className="gap-1"
            >
              View All
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {stats.ledgerEvents === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No ledger events yet</p>
                <p className="text-xs mt-1">
                  Get started by adding shareholders and issuing shares
                </p>
              </div>
            ) : (
              <LedgerEventsFeed companyId={tenantId} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Shareholder Dialog */}
      <ShareholderDialog
        open={shareholderDialogOpen}
        onOpenChange={setShareholderDialogOpen}
        onSuccess={() => {
          fetchStats();
          fetchCompanies();
        }}
        companies={companies}
        preselectedCompanyId={tenantId}
      />
    </DashboardLayout>
  );
};

export default Dashboard;
